import { useEffect, useState } from 'react';
import { Store, Hash, Layers, Mail, CheckCircle2, PauseCircle, Loader2, Plug } from 'lucide-react';
import { Badge, EmptyState, cn } from '@/components/ui';

/*
  Marketplace (Tier 1, 2026-05-24):
  Реальный listing из /api/marketplace/items. Показывает:
   - Adapters (Slack, далее Jira, NetBox, …) — драйверы к внешним системам
   - Modules — user-facing сервисы

  Actions per category:
   - Adapter: «Подключить» (если not_configured) / «Отключить» / «Настроить»
   - Module: «Установить в workspace» / «Удалить из workspace» / «Настроить»

  Tier 1 — простой enable/disable toggle на org-level (status='published'
  vs 'archived'). RBAC enforcement в Tier 2 (только org_admin может).
  Per-user workspace install — тоже Tier 2.
*/

interface MarketplaceItem {
  id: number;
  organization_id: number;
  name: string;
  version: string;
  kind: 'adapter' | 'native' | 'declarative_handwritten' | 'declarative_ai' | string;
  display_name: string;
  description: string;
  status: 'published' | 'archived' | 'deprecated' | 'draft' | string;
  icon?: string;
  author?: string;
  created_at: string;
  updated_at: string;
}

interface MarketplaceResp {
  items: MarketplaceItem[];
}

async function readMarketplaceItems(): Promise<MarketplaceItem[]> {
  const resp = await fetch('/api/marketplace/items', { credentials: 'include' });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  const data: MarketplaceResp = await resp.json();
  return data.items ?? [];
}

export function Marketplace() {
  const [items, setItems] = useState<MarketplaceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Record<number, boolean>>({});

  useEffect(() => {
    void readMarketplaceItems()
      .then(
        (loaded) => {
          setItems(loaded);
          setError(null);
        },
        (err: unknown) => setError(String(err)),
      )
      .finally(() => setLoading(false));
  }, []);

  async function toggle(item: MarketplaceItem) {
    setBusy((b) => ({ ...b, [item.id]: true }));
    const action = item.status === 'published' ? 'disable' : 'enable';
    try {
      const resp = await fetch(`/api/marketplace/items/${item.id}/${action}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const updated: MarketplaceItem = await resp.json();
      setItems((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));
    } catch (err) {
      console.warn('toggle failed', err);
      alert('Не удалось переключить статус: ' + String(err));
    } finally {
      setBusy((b) => ({ ...b, [item.id]: false }));
    }
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl px-8 py-10">
        <header className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight">Marketplace</h1>
        </header>
        <div className="flex items-center gap-2 text-sm text-fg-subtle">
          <Loader2 size={16} className="animate-spin" />
          Загрузка каталога…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-5xl px-8 py-10">
        <h1 className="mb-4 text-2xl font-semibold tracking-tight">Marketplace</h1>
        <div className="rounded-lg border notice-error-opaque p-4 text-sm">
          Не удалось загрузить каталог: {error}
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-5xl px-8 py-10">
        <header className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight">Marketplace</h1>
        </header>
        <EmptyState
          icon={<Store />}
          title="Каталог пуст"
          description="Будут показаны адаптеры и модули как только они появятся в реестре Kaname."
          action={{ label: 'Создать свой', to: '/portfolio/me' }}
        />
      </div>
    );
  }

  const adapters = items.filter((it) => it.kind === 'adapter');
  const modules = items.filter((it) => it.kind !== 'adapter');

  return (
    <div className="mx-auto max-w-5xl px-8 py-10">
      <header className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight">Marketplace</h1>
        <p className="mt-1 text-sm text-fg-muted">
          Адаптеры (драйверы к внешним системам) и модули (фичи поверх адаптеров).
        </p>
      </header>

      {adapters.length > 0 && (
        <Section title="Подключения" subtitle="Драйверы к внешним системам — Slack, Jira, NetBox, …">
          <div className="grid gap-3 sm:grid-cols-2">
            {adapters.map((it) => (
              <ItemCard key={it.id} item={it} busy={!!busy[it.id]} onToggle={() => toggle(it)} />
            ))}
          </div>
        </Section>
      )}

      {modules.length > 0 && (
        <Section title="Модули" subtitle="User-facing фичи: используют один или несколько адаптеров.">
          <div className="grid gap-3 sm:grid-cols-2">
            {modules.map((it) => (
              <ItemCard key={it.id} item={it} busy={!!busy[it.id]} onToggle={() => toggle(it)} />
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-10">
      <header className="mb-3">
        <h2 className="text-xs font-semibold tracking-wider text-fg-subtle uppercase">{title}</h2>
        <p className="mt-0.5 text-sm text-fg-muted">{subtitle}</p>
      </header>
      {children}
    </section>
  );
}

function ItemCard({ item, busy, onToggle }: { item: MarketplaceItem; busy: boolean; onToggle: () => void }) {
  const isAdapter = item.kind === 'adapter';
  const isActive = item.status === 'published';

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-lg border p-4',
        'border-line bg-card',
        // Sprint 2026-05-24: lift на hover как у Home QuickCards.
        // Marketplace card = focal element, scale 1.01 — меньше, чем у
        // Card.interactive (1.015).
        'transition-card-surface duration-slow ease-out-expo',
        'hover:scale-101 hover:border-line-strong',
        'hover:shadow-market-card',
        !isActive && 'opacity-60',
      )}
    >
      <div className="flex items-start gap-3">
        <span
          className={cn(
            'grid size-10 shrink-0 place-items-center rounded-lg',
            isAdapter ? 'service-enabled' : 'service-available',
          )}
        >
          <IconByName name={item.icon} fallbackKind={item.kind} />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-fg">{item.display_name}</h3>
            <StatusBadge status={item.status} />
          </div>
          <p className="mt-0.5 text-xs text-fg-subtle">
            {item.name}@{item.version} · {isAdapter ? 'adapter' : item.kind}
          </p>
        </div>
      </div>

      <p className="text-sm leading-snug text-fg-muted">{item.description}</p>

      <div className="mt-1 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onToggle}
          disabled={busy}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
            'border outline-none',
            isActive
              ? 'border-line-strong text-fg-muted hover:bg-action-hover'
              : 'border-brand-accent bg-brand-accent text-white hover:bg-brand-action',
            busy && 'cursor-not-allowed opacity-50',
          )}
        >
          {busy ? (
            <Loader2 size={12} className="animate-spin" />
          ) : isActive ? (
            <PauseCircle size={12} />
          ) : (
            <CheckCircle2 size={12} />
          )}
          {isActive ? (isAdapter ? 'Отключить' : 'Деактивировать') : isAdapter ? 'Включить' : 'Активировать'}
        </button>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'published':
      return (
        <Badge variant="success" dot>
          активен
        </Badge>
      );
    case 'archived':
      return (
        <Badge variant="neutral" dot>
          отключён
        </Badge>
      );
    case 'deprecated':
      return (
        <Badge variant="warn" dot>
          deprecated
        </Badge>
      );
    default:
      return (
        <Badge variant="neutral" dot>
          {status}
        </Badge>
      );
  }
}

function IconByName({ name, fallbackKind }: { name?: string; fallbackKind: string }) {
  switch (name) {
    case 'hash':
      return <Hash size={20} />;
    case 'mail':
      return <Mail size={20} />;
    case 'layers':
      return <Layers size={20} />;
    case 'plug':
      return <Plug size={20} />;
    default:
      // Fallback по kind
      return fallbackKind === 'adapter' ? <Plug size={20} /> : <Store size={20} />;
  }
}
